import { createApiClient, NetworkError, type ApiClient } from '@recipes/api-client';
import type { AuthTokens, CurrentUser, LoginRequest, RegisterRequest } from '@recipes/contracts';
import i18n from '../i18n';
import { nativeSession } from './native-session';

export interface AuthState {
  status: 'loading' | 'authenticated' | 'anonymous';
  user: CurrentUser | null;
  accessToken: string | null;
  accessTokenExpiresAt: number | null;
  /** Tries the refresh cookie; resolves with a new access token or null. */
  refresh: () => Promise<string | null>;
  login: (input: LoginRequest) => Promise<CurrentUser>;
  register: (
    input: RegisterRequest,
  ) => Promise<{ user: CurrentUser; verificationRequired: boolean }>;
  logout: () => Promise<void>;
  setUser: (user: CurrentUser) => void;
  bootstrap: () => Promise<void>;
}

type Listener = (state: AuthState) => void;

/**
 * Tiny framework-free store (subscribe/getState) so the API client can read the token
 * without React. Exposed to React through useSyncExternalStore in auth-provider.tsx.
 */
function createAuthStore() {
  const listeners = new Set<Listener>();
  const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  let refreshPromise: Promise<string | null> | null = null;
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;

  // A bare client used only for auth endpoints (never retries through itself).
  const authApi: ApiClient = createApiClient({
    baseUrl,
    getAccessToken: () => state.accessToken,
    headers: () => ({ 'Accept-Language': i18n.language || 'de', ...nativeSession.headers() }),
  });

  const emit = () => listeners.forEach((l) => l(state));

  /** Last known user, kept so the app stays usable offline (spec 15). Non-sensitive fields only. */
  const readCachedUser = (): CurrentUser | null => {
    try {
      if (localStorage.getItem('rm.hasSession') !== '1') return null;
      const raw = localStorage.getItem('rm.user');
      return raw ? (JSON.parse(raw) as CurrentUser) : null;
    } catch {
      return null;
    }
  };

  /** Authenticated-but-offline: no access token, reads come from the local copies. */
  const restoreCachedSession = (): boolean => {
    const cached = readCachedUser();
    if (!cached) return false;
    set({ status: 'authenticated', user: cached, accessToken: null, accessTokenExpiresAt: null });
    return true;
  };
  const set = (patch: Partial<AuthState>) => {
    state = { ...state, ...patch };
    emit();
  };

  const scheduleRefresh = (expiresAtIso: string) => {
    if (refreshTimer) clearTimeout(refreshTimer);
    const ms = new Date(expiresAtIso).getTime() - Date.now() - 30_000;
    refreshTimer = setTimeout(() => void state.refresh(), Math.max(5_000, ms));
  };

  const applyTokens = (tokens: AuthTokens) => {
    set({
      status: 'authenticated',
      user: tokens.user,
      accessToken: tokens.accessToken,
      accessTokenExpiresAt: new Date(tokens.accessTokenExpiresAt).getTime(),
    });
    scheduleRefresh(tokens.accessTokenExpiresAt);
    // Native shell only: the rotated refresh token goes into Keychain/Keystore.
    if (tokens.refreshToken) void nativeSession.write(tokens.refreshToken);
    try {
      localStorage.setItem('rm.hasSession', '1');
      localStorage.setItem('rm.user', JSON.stringify(tokens.user));
    } catch {
      // ignore storage failures
    }
  };

  const clear = () => {
    if (refreshTimer) clearTimeout(refreshTimer);
    set({ status: 'anonymous', user: null, accessToken: null, accessTokenExpiresAt: null });
    void nativeSession.clear();
    try {
      localStorage.removeItem('rm.hasSession');
      localStorage.removeItem('rm.user');
    } catch {
      // ignore
    }
  };

  let state: AuthState = {
    status: 'loading',
    user: null,
    accessToken: null,
    accessTokenExpiresAt: null,
    refresh: async () => {
      if (!refreshPromise) {
        refreshPromise = nativeSession
          .read()
          .then((stored) => {
            // Browsers: the HttpOnly cookie carries the token. Native: no stored token, no session.
            if (nativeSession.enabled && !stored) throw new Error('No refresh token');
            return authApi.auth.refresh(stored ?? undefined);
          })
          .then((tokens) => {
            applyTokens(tokens);
            return tokens.accessToken;
          })
          .catch((error: unknown) => {
            // Offline or server unreachable: keep the session so cached recipes stay readable.
            if (error instanceof NetworkError && restoreCachedSession()) return null;
            clear();
            return null;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }
      return refreshPromise;
    },
    login: async (input) => {
      const tokens = await authApi.auth.login(input);
      applyTokens(tokens);
      return tokens.user;
    },
    register: async (input) => {
      const result = await authApi.auth.register(input);
      if (result.tokens) applyTokens(result.tokens);
      return { user: result.user, verificationRequired: result.verificationRequired };
    },
    logout: async () => {
      try {
        await authApi.auth.logout((await nativeSession.read()) ?? undefined);
      } finally {
        clear();
      }
    },
    setUser: (user) => set({ user }),
    bootstrap: async () => {
      let hasSession = false;
      try {
        hasSession = localStorage.getItem('rm.hasSession') === '1';
      } catch {
        hasSession = true;
      }
      if (!hasSession) {
        clear();
        return;
      }
      if (typeof navigator !== 'undefined' && !navigator.onLine && restoreCachedSession()) return;
      await state.refresh();
    },
  };

  return {
    getState: () => state,
    subscribe: (listener: Listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export const authStore = createAuthStore();
